import React, { useState, useEffect } from "react";
import { Box, Paper, Button, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux"; // Importing Redux hooks
import { setPage, setRowsPerPage } from "../../store/slices/paginationSlice"; // Import pagination actions
import { fetchRentalPropertyList } from "../../services/api/rental-property";
import RentalPropertyTable from "./RentalPropertyTable";
import RentalPropertyFilters from "./RentalPropertyFilters";
import PaginationManager from "../../components/newComp/Pagination";
import { showToast } from "../../store/slices/toastSlice";
import { fetchBuildingList } from "../../services/api/building";

const RentalPropertyList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { page, rowsPerPage } = useSelector((state) => state.pagination);

  const [rentalPropertyList, setRentalPropertyList] = useState([]);
  const [filteredRentalPropertyList, setFilteredRentalPropertyList] = useState(
    []
  );
  const [buildingList, setBuildingList] = useState([]);
  const [selectBuildings, setSelectBuildings] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const rentalPropertyList = await fetchRentalPropertyList();
        setRentalPropertyList(rentalPropertyList.data);
        setFilteredRentalPropertyList(rentalPropertyList.data);

        const buildingList = await fetchBuildingList();
        setBuildingList(buildingList.data);
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    // Filter the buildings based on selected building IDs
    if (selectBuildings.length > 0) {
      const selectedBuildingIds = selectBuildings.map(
        (building) => building.id
      );

      const filteredData = rentalPropertyList.filter((property) =>
        selectedBuildingIds.includes(property.building_id)
      );

      setFilteredRentalPropertyList(filteredData); // Update the filtered list
    } else {
      setFilteredRentalPropertyList(rentalPropertyList); // No filter, show all data
    }
  }, [selectBuildings, rentalPropertyList]);

  const handleChangePage = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    dispatch(setPage(0));
  };

  const currentPageData = filteredRentalPropertyList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Box
        sx={{ width: "100%", p: { xs: 2, md: 3 }, backgroundColor: "#f7f8fa" }}
      >
        {/* Filter Section */}
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            mb: 3,
            p: 2.5,
            borderRadius: 2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          }}
        >
          <RentalPropertyFilters
            buildingList={buildingList}
            selectBuildings={selectBuildings}
            setSelectBuildings={setSelectBuildings}
            handleExport={() => {}}
          />
        </Paper>

        {/* Top Action Bar */}
        {/* Top Bar: Title + Add Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Rental Property List
            </Typography>
          </Box>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#024ad7",
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                backgroundColor: "#fec01f",
                color: "#000",
              },
            }}
            onClick={() =>
              navigate("/rental-property", {
                state: { from: "/rental-property-list" },
              })
            }
          >
            + Add New Property
          </Button>
        </Box>

        {/* Table Section */}
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              p: 2,
            }}
          >
            <RentalPropertyTable rows={currentPageData} />
          </Box>

          <Divider />

          <PaginationManager
            count={filteredRentalPropertyList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </>
  );
};

export default RentalPropertyList;
